import { useParams } from 'wouter';
import { AdminOrganizations } from '@platform/client';
import { VettingStatus } from '@platform/client/schemas';
import {
  Button,
  Modal,
  ModalCallToAction,
  InputDropdown,
  InputDropdownItem,
  CheckBox,
  Spinner,
} from '@platform/components';
import { useState, useContext, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GreenCircleCheckmark } from '../../../svg/GreenCircleCheckmark';
import { AdminTableContext } from '../../AdminTable';
import cx from 'classix';
import { ReviewPageWrapper, ReviewItem } from '../../AdminViewPage';

export const OrganizationVetting = () => {
  const { adminQueryOptions } = useContext(AdminTableContext);
  const { organizationId } = useParams();
  const {
    data: organization,
    isLoading,
    isError,
    isSuccess,
  } = AdminOrganizations.useGetReviewData({
    organization_id: Number(organizationId),
  });
  const [vettingModalIsOpen, setVettingModalIsOpen] = useState(false);
  const [sendEmailModalIsOpen, setSendEmailModalIsOpen] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!vettingModalIsOpen) {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [vettingModalIsOpen]);

  if (!organization) {
    return null;
  }
  return (
    <ReviewPageWrapper
      title={organization.name}
      isLoading={isLoading}
      isError={isError}
      isSuccess={isSuccess}
      topRef={topRef}
    >
      <div
        className={cx(
          'max-w-1/2 text-lg text-center mx-auto rounded-md px-2 italic',
          organization.vettingStatus === VettingStatus.transform && 'bg-success-500 text-white',
          organization.vettingStatus === VettingStatus.match && 'bg-success-100 text-black',
          organization.vettingStatus === VettingStatus.pending && 'bg-warning-100',
          organization.vettingStatus === VettingStatus.rejected && 'bg-error text-white',
          organization.vettingStatus === VettingStatus.signup_incomplete &&
            'bg-gray-200 text-white',
        )}
      >
        <h2>Current Status: {organization.vettingStatus}</h2>
      </div>
      <ReviewItem label='Location'>{organization.country}</ReviewItem>
      <ReviewItem label='Type'>{organization.type}</ReviewItem>
      <ReviewItem label='Size'>{organization.size}</ReviewItem>
      <ReviewItem label='Industries'>
        <div className='inline'>
          {organization.industries.map((industry, index) => (
            <span key={`${index}-${industry}`}>
              {industry}
              {index < organization.industries.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      </ReviewItem>
      <ReviewItem label='Causes'>
        <div className='inline'>
          {organization.causes.map((cause, index) => (
            <span key={`${index}-${cause}`}>
              {cause}
              {index < organization.causes.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      </ReviewItem>
      <ReviewItem label='Website'>
        {organization.website && (
          <a
            className='text-brand-500 hover:underline'
            href={
              organization.website.startsWith('http') ?
                organization.website
              : `https://${organization.website}`
            }
            target='_blank'
          >
            {organization.website}
          </a>
        )}
      </ReviewItem>
      <ReviewItem label='CRM'>
        <a
          className='hover:underline text-brand-500'
          href={`https://crm.zoho.com/crm/org667587627/search?searchword=${encodeURIComponent(organization.name)}`}
          target='_blank'
        >
          Organization Search
        </a>
      </ReviewItem>
      <ReviewItem label='Description'>
        <p dangerouslySetInnerHTML={{ __html: organization.description }} />
      </ReviewItem>
      <ReviewItem label='Impact Story'>
        <p dangerouslySetInnerHTML={{ __html: organization.impactStory }} />
      </ReviewItem>
      <div className='flex justify-between gap-2 mt-3'>
        <Button variant='secondary-color' size='md' href='/organizations'>
          Back to Organizations
        </Button>
        <div className='flex gap-4'>
          <Button size='sm' variant='tertiary-danger' onPress={() => setSendEmailModalIsOpen(true)}>
            Request Website
          </Button>
          <Button
            variant='primary'
            size='md'
            onPress={() => {
              setVettingModalIsOpen(true);
            }}
          >
            Update Vetting
          </Button>
        </div>
      </div>
      <VettingModal
        isOpen={vettingModalIsOpen}
        onClose={() => setVettingModalIsOpen(false)}
        organizationId={organization.organizationId}
        vettingStatus={organization.vettingStatus}
        page={adminQueryOptions.page}
        limit={adminQueryOptions.limit}
      />
      <SendEmailModal
        isOpen={sendEmailModalIsOpen}
        onClose={() => setSendEmailModalIsOpen(false)}
        organizationId={organization.organizationId}
      />
    </ReviewPageWrapper>
  );
};

type VettingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: number;
  vettingStatus: VettingStatus;
  page: number;
  limit: number;
};

export const VettingModal = (props: VettingModalProps) => {
  const { isOpen, onClose, organizationId, vettingStatus, page, limit } = props;
  const [result, setResult] = useState(vettingStatus);
  const [sendEmail, setSendEmail] = useState(false);
  const [modalStage, setModalStage] = useState<'decision' | 'updating' | 'success' | 'error'>(
    'decision',
  );
  const onModalClose = () => {
    setModalStage('decision');
    setSendEmail(false);
    onClose();
  };

  const queryClient = useQueryClient();
  const vettingMutator = AdminOrganizations.useUpdateOrgVetting({
    mutation: {
      onMutate: () => {
        setModalStage('updating');
      },
      onError: () => {
        setModalStage('error');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminOrganizations.getGetOrganizationsQueryKey({
              page: page,
              limit: limit,
            }),
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminOrganizations.getGetReviewDataQueryKey({
              organization_id: organizationId,
            }),
          ],
        });
        setModalStage('success');
      },
    },
  });
  const primaryCTA: ModalCallToAction =
    modalStage === 'decision' ?
      {
        buttonVariant: 'primary',
        text: 'Save Vetting',
        callback: () => {
          if (result) {
            vettingMutator.mutate({
              data: {
                organizationId,
                result,
                sendEmail: result !== VettingStatus.pending ? sendEmail : false,
              },
            });
          }
        },
      }
    : {
        buttonVariant: 'primary',
        text: 'Close',
        callback: () => {
          setModalStage('decision');
          props.onClose();
        },
        isDisabled: () => modalStage === 'updating',
      };

  return (
    <Modal
      variant='dialog'
      headerDivision='divider'
      isOpen={isOpen}
      title='Organization Vetting'
      onClose={() => onModalClose()}
      primaryCTA={primaryCTA}
      secondaryCTA={
        modalStage === 'decision' ? { text: 'Cancel', callback: () => onModalClose() } : undefined
      }
      ignoreExtraPadding
    >
      <div className='flex flex-col gap-6'>
        {modalStage === 'decision' && (
          <div className='flex flex-col gap-8'>
            <VettingResultDropdown result={result} setResult={setResult} />
            {result && result !== VettingStatus.pending && (
              <CheckBox
                size='md'
                description='Notify organization'
                aria-label='send email checkbox'
                onChange={() => setSendEmail((prev) => !prev)}
                isSelected={sendEmail}
              />
            )}
          </div>
        )}
        {modalStage === 'updating' && (
          <div className='flex flex-col gap-4 items-center'>
            <Spinner />
          </div>
        )}
        {modalStage === 'success' && (
          <div className='flex flex-col gap-4 items-center'>
            <GreenCircleCheckmark size={100} />
            Vetting updated successfully.
          </div>
        )}
        {modalStage === 'error' && <div className='text-center'>Something went wrong</div>}
      </div>
    </Modal>
  );
};

const resultItems: InputDropdownItem[] = Object.values(VettingStatus).map((status) => ({
  textValue: status,
  text: status.split('_').join(' '),
}));

const VettingResultDropdown = ({
  result,
  setResult,
}: {
  result?: VettingStatus;
  setResult: (key: VettingStatus) => void;
}) => {
  return (
    <InputDropdown
      label='Select result'
      type='default'
      defaultSelectedKey={result}
      onSelectionChange={(key) => setResult(key as VettingStatus)}
      items={resultItems}
    />
  );
};

const SendEmailModal = ({
  isOpen,
  onClose,
  organizationId,
}: {
  isOpen: boolean;
  onClose: () => void;
  organizationId: number;
}) => {
  const [modalStage, setModalStage] = useState<'confirmation' | 'sending' | 'success' | 'error'>(
    'confirmation',
  );
  const sendEmailMutator = AdminOrganizations.useSendProvideWebsiteEmail({
    mutation: {
      onMutate: () => {
        setModalStage('sending');
      },
      onError: () => {
        setModalStage('error');
      },
      onSuccess: () => {
        setModalStage('success');
      },
    },
  });
  const closeModal = () => {
    setModalStage('confirmation');
    onClose();
  };

  const primaryCTA: ModalCallToAction =
    modalStage === 'confirmation' ?
      {
        buttonVariant: 'primary',
        text: 'Send',
        callback: () => {
          sendEmailMutator.mutate({
            params: { organization_id: organizationId },
          });
        },
      }
    : {
        text: 'Close',
        callback: () => closeModal(),
      };

  const secondaryCTA: ModalCallToAction = {
    text: 'Cancel',
    callback: () => closeModal(),
  };

  return (
    <Modal
      variant='dialog'
      headerDivision='divider'
      isOpen={isOpen}
      title='Request Website'
      onClose={closeModal}
      primaryCTA={{ ...primaryCTA, isDisabled: () => modalStage === 'sending' }}
      secondaryCTA={modalStage === 'confirmation' ? secondaryCTA : undefined}
      ignoreExtraPadding
    >
      <div className='flex justify-center sm:min-w-[250px]'>
        {modalStage === 'confirmation' && <div>Send email requesting valid website?</div>}
        {modalStage === 'sending' && <Spinner />}
        {modalStage === 'success' && (
          <div className='flex flex-col gap-2 items-center'>
            <GreenCircleCheckmark size={100} />
            Email sent successfully.
          </div>
        )}
        {modalStage === 'error' && <div className='text-center'>Something went wrong</div>}
      </div>
    </Modal>
  );
};
